// AGC: Despesas - Expenses list screen
// AGC: Shows all expense entries with ability to add new ones
// AGC: All expenses are filtered by user account - each user only sees their own expenses
import React, { useState, useEffect } from 'react'
import { DollarSign, Plus, ChevronLeft, TrendingDown } from 'lucide-react'
import { Card, BottomNav } from '../shared'
import { formatCurrency, formatDate } from '@droidfarm/shared'
import type { Screen, Expense } from '../../types'
import { getExpenses } from '../../services/expenses'
import { useAuth } from '../../hooks/useAuth'

/**
 * AGC: Format large currency values with K/M suffixes for compact display
 */
const formatLargeValue = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 10000) {
    return `R$ ${(value / 1000).toFixed(0)}K`
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)}K`
  }
  return formatCurrency(value)
}

interface DespesasProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

/**
 * AGC: Despesas - Shows all expense entries
 * AGC: Has "+" button to add new expenses
 */
export const Despesas: React.FC<DespesasProps> = ({ onNavigate, onBack }) => {
  const { getAuthToken } = useAuth()
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // AGC: Load expenses from Firebase for the authenticated user
  useEffect(() => {
    const loadExpenses = async () => {
      try {
        setIsLoading(true)
        // AGC: Get auth token and fetch expenses filtered by user account
        const token = await getAuthToken()
        const data = await getExpenses(token)
        setExpenses(data)
      } catch (error) {
        console.error('AGC: Error loading expenses:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadExpenses()
  }, [getAuthToken])

  // AGC: Calculate total expenses
  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.value, 0)

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-20">
      {/* AGC: Top Bar */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Despesas</h1>
        </div>
      </div>

      {/* AGC: Total Summary Card - Matching dashboard layout */}
      <div className="px-4 pt-3 pb-2">
        <Card padding="none" className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800">
          <div className="p-3 flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <TrendingDown className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                <p className="text-[10px] text-red-700 dark:text-red-300 font-semibold uppercase tracking-wider">
                  Total de Despesas
                </p>
              </div>
              <p className="text-xl font-bold text-red-800 dark:text-red-200 tabular-nums truncate">
                {formatLargeValue(totalExpenses)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-800/30 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* AGC: Expenses List */}
      <div className="px-4 py-4 space-y-3">
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Carregando despesas...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-2">Nenhuma despesa cadastrada</p>
            <p className="text-sm text-gray-400">Toque no botão + para adicionar uma despesa</p>
          </div>
        ) : (
          expenses.map((expense) => (
            <ExpenseCard key={expense.id} expense={expense} />
          ))
        )}
      </div>

      {/* AGC: FAB - Add new expense (safe-area-bottom keeps it above BottomNav + Android nav bar) */}
      <button
        onClick={() => onNavigate('new-expense')}
        className="fixed right-4 w-14 h-14 bg-accent-500 hover:bg-accent-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors z-[90]"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
        aria-label="Adicionar despesa"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* AGC: Bottom Navigation */}
      <BottomNav 
        active="dashboard" 
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard')
          else if (tab === 'fields') onNavigate('fields')
          else if (tab === 'tasks') onNavigate('tasks')
          else if (tab === 'weather') onNavigate('weather')
        }} 
      />
    </div>
  )
}

/**
 * AGC: Capitalize first letter of a string (preserve rest of case)
 */
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * AGC: Expense Card Component
 */
interface ExpenseCardProps {
  expense: Expense
}

const ExpenseCard: React.FC<ExpenseCardProps> = ({ expense }) => {
  // AGC: Capitalize first letter of expense title
  const capitalizedTitle = capitalizeFirstLetter(expense.title)

  return (
    <Card hover padding="none">
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          {/* AGC: Left side - info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">{capitalizedTitle}</h3>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {formatDate(expense.date)}
            </span>
          </div>
          
          {/* AGC: Right side - value */}
          <div className="flex-shrink-0">
            <p className="text-sm font-bold text-red-600 dark:text-red-400 tabular-nums">
              -{formatLargeValue(expense.value)}
            </p>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default Despesas

